/**
 * Org structure.
 *
 * Four ways to read the same tree, in one screen:
 *
 *   Hierarchy   top-down chart, a card per unit
 *   Lateral     the same structure left-to-right, so long names fit
 *   Compact     top-down until the last level, where peers stack as rows
 *   Compare     two to twelve units side by side, each with its whole lineage
 *
 * The chart shapes share a layout engine (`org-layout.ts`) and a model
 * (`org-model.ts`); this file is the shell — what is fetched, what is filtered,
 * what is selected, and which controls the current view can actually use.
 *
 * The page header is closed by default. Its title, figures and project picker
 * are reference rather than tools, and the chart wants the room; the toggle
 * carries the one figure worth seeing while it is shut.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Toast } from 'sharedUi/Toast';
import { useOrgStructure } from '../../hooks/useOrgStructure';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { unitsService } from '../../services/units.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { encodeEntityId } from '../../constants/entity-links';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { useDebounce } from '../../hooks/useDebounce';
import Icon from './org-icons';
import OrgChart from './org-chart';
import OrgCompare, { MAX_COMPARE } from './org-compare';
import OrgInspector from './org-inspector';
import OrgOutline from './org-outline';
import OrgSearch from './org-search';
import { useFullscreen } from './use-fullscreen';
import { buildOrgModel, indexPersonnel, nf, headColour, revealFlow, vacancyTone, peopleLabel } from './org-model';
import type { ChartView } from './org-layout';
import styles from './org-structure.module.css';

type ViewKey = ChartView | 'outline' | 'compare';

const VIEWS: Array<{ key: ViewKey; label: string; group: string; what: string }> = [
  {
    key: 'chart',
    label: 'Hierarchy',
    group: 'Chart shape',
    what: 'Top-down chart. Each unit is a card with its officer in charge; the lines are the reporting relationship.',
  },
  {
    key: 'ribbon',
    label: 'Lateral',
    group: 'Chart shape',
    what: 'The same structure left-to-right, so deep chains and long unit names fit without wrapping.',
  },
  {
    key: 'compact',
    label: 'Compact',
    group: 'Chart shape',
    what: 'Top-down until the last level, where peers list beneath their parent — what printed charts do for many siblings.',
  },
  {
    key: 'outline',
    label: 'Outline',
    group: 'List',
    what: 'The indented tree, on the same data as the charts. Fastest way to read straight down a large force, and the only view that shows every figure for every unit at once.',
  },
  {
    key: 'compare',
    label: 'Compare',
    group: 'Side by side',
    what: 'Two to twelve units side by side, each showing its whole line of descent — for checking one district against another without losing your place.',
  },
];

const isChartView = (v: ViewKey): v is ChartView =>
  v === 'chart' || v === 'ribbon' || v === 'compact';


/**
 * Where the chart opens: the top two levels.
 *
 * `depth` is zero-indexed and the level control reads `depth + 1`, so this is
 * the "2 of N levels" the toolbar shows on a fresh tree — roughly 8 units on
 * the live force. It opened on three levels (17 units) before; the + button
 * still steps down one level at a time with the unit count beside it, so the
 * wider view is one press away.
 */
const FEW_DEPTH = 1;

const RAIL_KEY = 'orgStructure.rail';
const HINT_KEY = 'orgStructure.hintSeen';

interface OrgStructureViewProps {
  /** Inside the Units tab card the page header is redundant, so it is dropped. */
  embedded?: boolean;
}

const OrgStructureView = ({ embedded = false }: OrgStructureViewProps) => {
  const navigate = useAppNavigate();
  const toast = useRef<Toast>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(shellRef);
  const canCreate = useCanCreate(JOB_NAMES.UNITS);
  const canUpdate = useCanUpdate(JOB_NAMES.UNITS);

  const [view, setView] = useState<ViewKey>('chart');
  const [depth, setDepth] = useState(FEW_DEPTH);
  const [rawQuery, setRawQuery] = useState('');
  const [vacantOnly, setVacantOnly] = useState(false);
  /**
   * Cut the tree down to the selected unit's line of command.
   *
   * Off by default now that the popup draws that line as a diagram. Picking a
   * unit leaves the chart where it is and marks the card, so you keep the
   * context you were reading; the flow you asked to see is in the popup, at a
   * size that needs no panning. The toggle is still here for anyone who wants
   * the chart itself reduced to one branch.
   */
  const [isolate, setIsolate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  /** The details popup. Closing it leaves the line on the chart. */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  /** What had focus before the popup took it, so it can be handed back. */
  const popReturn = useRef<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [columns, setColumns] = useState<string[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);
  // Two one-shot counters drive the camera: `fit` re-frames the whole chart,
  // `focus` frames one unit's flow. Counters rather than values, so clearing
  // them can never re-trigger the effect that consumed them.
  // Carries a framing mode with the counter: 'fit' fills the plane (load, reset,
  // project switch — few units, so it stays readable and never opens tiny in a
  // corner); 'home' keeps the fixed opening zoom so revealing deeper levels does
  // not shrink a large tree. Both update together, so the chart reads them as one.
  const [frame, setFrame] = useState<{ token: number; mode: 'fit' | 'home' }>({ token: 0, mode: 'fit' });
  const fitToken = frame.token;
  const [focus, setFocus] = useState<{ id: string; token: number } | null>(null);
  const focusSeq = useRef(0);
  const reframe = useCallback(
    (mode: 'fit' | 'home' = 'fit') => setFrame((f) => ({ token: f.token + 1, mode })),
    []
  );
  const frameFlow = useCallback((id: string) => {
    focusSeq.current += 1;
    setFocus({ id, token: focusSeq.current });
  }, []);
  // Both of these are a preference, not a session detail — someone who closed
  // the rail or dismissed the hint meant it for next time too.
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem(RAIL_KEY) !== 'closed');
  const [hintSeen, setHintSeen] = useState(() => localStorage.getItem(HINT_KEY) === 'yes');

  const query = useDebounce(rawQuery, 250).toLowerCase().trim();


  /*
   * How tall the module can be, measured rather than assumed.
   *
   * A chart is pannable, so it has no natural height to grow to and something
   * has to give it one. Both call sites used to do that with a viewport
   * subtraction tuned to the chrome above them, and the sum was wrong on at
   * least one of them — the shell overran the page and took the zoom box, the
   * Fit button and the compare tray below the fold with it.
   *
   * The element's own offset from the top of the document answers the question
   * exactly. Document offset, not viewport offset, so scrolling the page does
   * not resize the chart underneath the pointer.
   */
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.setProperty('--org-avail', `${Math.max(360, window.innerHeight - top - 12)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    // Anything above the module changing size moves it down the page.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, []);

  // Project scope: default to the caller's own project. A cross-project admin
  // can switch or view all accessible; a single-project user gets no picker
  // because the backend already limits them to their own tree.
  const [projectId, setProjectId] = useState<string | null>(localStorage.getItem('appId') || null);
  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  /*
   * Two sources, merged by unit id.
   *
   * The units list is the structure: the same call the Organisation Hierarchy
   * screen makes, so both screens show the same units under the same access
   * rules, and it is the only one that carries the embedded posts (sanctioned
   * strength) and does not drop inactive units.
   *
   * The org-structure endpoint is enrichment: personnel headcount, which
   * nothing else computes. If it fails the chart still draws — without the
   * People figures rather than not at all.
   */
  const units = useQuery({
    queryKey: ['units', 'org-tree', projectId],
    queryFn: () => unitsService.getUnitTree(projectId || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const headcount = useOrgStructure({ format: 'tree', appId: projectId || undefined });

  const personnel = useMemo(
    () => indexPersonnel((headcount.data?.units || []) as Parameters<typeof indexPersonnel>[0]),
    [headcount.data]
  );
  const model = useMemo(() => buildOrgModel(units.data || [], personnel), [units.data, personnel]);

  const isLoading = units.isLoading;
  const isFetching = units.isFetching || headcount.isFetching;
  const error = units.error as Error | null;
  const refetch = useCallback(() => {
    units.refetch();
    headcount.refetch();
  }, [units, headcount]);

  const notify = useCallback((detail: string) => {
    toast.current?.show({ severity: 'info', summary: '', detail, life: 2600 });
  }, []);

  /* ── depth: collapse everything at or below the chosen level ── */
  const applyDepth = useCallback(
    (d: number) => {
      setCollapsed(new Set(model.ids.filter((id) => model.byId[id].lvl >= d && model.byId[id].kids.length)));
    },
    [model]
  );

  /*
   * A fresh tree opens at the top two levels, at a fixed zoom.
   *
   * Keyed on the units payload, NOT on the merged model. The model is memoised
   * on the headcount response too, so keying it there ran this a second time
   * when that slower request landed — clearing the selection, resetting every
   * folded branch and yanking the camera back to Fit, seconds after the user
   * had started working. React Query's structural sharing keeps this reference
   * stable across a refetch that changes nothing, so a poll does not reset you
   * either. Headcount arriving repaints figures and nothing else.
   */
  const structure = units.data;
  useEffect(() => {
    if (!model.ids.length) return;
    setDepth(FEW_DEPTH);
    applyDepth(FEW_DEPTH);
    setSelected(null);
    setDetailsOpen(false);
    reframe();
    setColumns((cols) => cols.filter((id) => model.byId[id]));
    // Everything read here is derived from `structure`; listing them would key
    // the effect on the headcount merge again, which is the bug above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure]);

  /*
   * How deep to unfold — one level at a time, with the consequence on screen.
   *
   * This has now been a dropdown of eight depths, then a Few/All toggle. The
   * dropdown asked you to choose a number before you could see anything; the
   * toggle replaced eight vague options with two vaguer ones — "few" is not a
   * quantity anybody can act on, and the gulf behind it was 17 units to 1,377.
   *
   * A step at a time, with the unit count updating beside it, means you never
   * choose blind: you open one more level, see what it costs, and stop when the
   * chart stops being useful. Which is the judgement the control was asking you
   * to make in the first place, only now you have the number in front of you.
   */
  const deepest = model.maxLevel;
  const step = useCallback(
    (d: number) => {
      const next = Math.min(deepest, Math.max(0, d));
      setDepth(next);
      applyDepth(next);
      // Fixed zoom, not fit: revealing a deeper level must not shrink the whole tree.
      reframe('home');
    },
    [deepest, applyDepth, reframe]
  );

  /** How many units that depth actually puts on the chart. */
  const shownUnits = useMemo(
    () => model.ids.reduce((n, id) => (model.byId[id].lvl <= depth ? n + 1 : n), 0),
    [model, depth]
  );

  /* ── the compare set ── */
  const toggleCompare = useCallback(
    (id: string) => {
      const u = model.byId[id];
      if (!u) return;
      setColumns((cols) => {
        const i = cols.indexOf(id);
        if (i >= 0) {
          notify(`Removed ${u.name} from the compare set`);
          return cols.filter((c) => c !== id);
        }
        if (cols.length >= MAX_COMPARE) {
          notify(`The compare set holds ${MAX_COMPARE} — remove one first`);
          return cols;
        }
        notify(`Added ${u.name} · ${cols.length + 1} stored`);
        return [...cols, id];
      });
    },
    [model, notify]
  );

  /**
   * Point column `index` at `id`.
   *
   * If that unit is already stored in another column, showing it twice is never
   * what was meant, so the older copy is dropped and the index it left behind
   * is accounted for.
   */
  const setColumn = useCallback((index: number, id: string) => {
    setColumns((cols) => {
      if (index < 0 || index >= cols.length) return cols;
      const next = [...cols];
      const dup = next.indexOf(id);
      if (dup === index) return cols;
      let target = index;
      if (dup >= 0) {
        next.splice(dup, 1);
        if (dup < target) target -= 1;
      }
      next[target] = id;
      return next;
    });
  }, []);

  const removeColumn = useCallback(
    (index: number) => {
      setColumns((cols) => {
        const id = cols[index];
        if (id && model.byId[id]) notify(`Removed ${model.byId[id].name} from the compare set`);
        return cols.filter((_, i) => i !== index);
      });
    },
    [model, notify]
  );

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Take me to this unit.
   *
   * On a chart that means opening its entire line of command — every ancestor
   * above it and what reports to it below — selecting it so the spotlight
   * isolates that line, and framing the whole thing. On Compare the same intent
   * is a column, so that is what it does there.
   */
  const goToUnit = useCallback(
    (id: string, switchToChart = false) => {
      if (!model.byId[id]) return;
      if (view === 'compare' && !switchToChart) {
        if (columns.includes(id)) return;
        if (columns.length >= MAX_COMPARE) setColumn(columns.length - 1, id);
        else toggleCompare(id);
        return;
      }
      if (switchToChart && view === 'compare') setView('chart');
      setCollapsed((c) => {
        const { collapsed: next, hidden } = revealFlow(model, id, c);
        if (hidden > 0) {
          notify(
            `Opened ${model.byId[id].name} · ${nf(hidden)} more below are still folded — use the + markers`
          );
        }
        return next;
      });
      setSelected(id);
      setDetailsOpen(true);
      frameFlow(id);
    },
    [view, columns, model, setColumn, toggleCompare, frameFlow, notify]
  );

  /**
   * Back to the whole tree.
   *
   * The camera has to be re-fitted, not left alone: the chart it was framing
   * was one line of command, and the plane it is going back to is the entire
   * force. Keeping the old zoom would drop you into an unrecognisable corner.
   */
  const clearSelection = useCallback(() => {
    setSelected(null);
    setDetailsOpen(false);
    // Back to the view you arrived at, not to wherever the last reveal left
    // the folded branches — otherwise "show everything" hands back a tree that
    // is nothing like the one you started from.
    // Back to the depth you arrived at, not to wherever the last reveal left
    // the folded branches — otherwise "clear" hands back a tree that is nothing
    // like the one you started from.
    setDepth(FEW_DEPTH);
    applyDepth(FEW_DEPTH);
    reframe();
  }, [reframe, applyDepth]);

  /**
   * The one way any view says "this unit".
   *
   * Clicking a card, picking a search result and following a neighbour in the
   * popup all land here, so they cannot drift apart: each opens the line,
   * frames it, and puts the details up. Re-picking what is already selected is
   * how you get the popup back after dismissing it.
   */
  const selectUnit = useCallback(
    (id: string | null) => {
      if (!id) clearSelection();
      else goToUnit(id);
    },
    [clearSelection, goToUnit]
  );

  /** From Compare or the tray: same thing, but land on the chart. */
  const showOnChart = useCallback((id: string) => goToUnit(id, true), [goToUnit]);

  /*
   * One Escape, one ladder, one listener.
   *
   * The popup gives way first, then the selection, then full screen — nearest
   * intent first, exactly as the search box already behaves. This used to be
   * two listeners on `document` (here and in the full-screen hook) with no
   * ordering between them, so a single press inside fallback full screen both
   * closed the popup and left full screen.
   */
  const fsFallback = fullscreen.mode === 'fallback';
  useEffect(() => {
    if (!selected && !fsFallback) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detailsOpen) setDetailsOpen(false);
      else if (selected) clearSelection();
      else fullscreen.exit();
      e.stopPropagation();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, detailsOpen, clearSelection, fsFallback, fullscreen]);

  /*
   * A dialog has to hold focus and give it back.
   *
   * It was announced as role="dialog" and behaved like nothing of the kind:
   * Tab walked straight out of it into the chart underneath, and closing it
   * dropped focus on the body, which sends a keyboard user back to the top of
   * the page.
   */
  useEffect(() => {
    if (!detailsOpen) return;
    popReturn.current = document.activeElement as HTMLElement | null;
    const box = popRef.current;
    const focusables = () =>
      Array.from(
        box?.querySelectorAll<HTMLElement>('button, [href], select, input, [tabindex]:not([tabindex="-1"])') || []
      ).filter((el) => !el.hasAttribute('disabled'));
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      popReturn.current?.focus?.();
    };
  }, [detailsOpen]);

  const openUnit = useCallback((id: string) => navigate(`/units/${encodeEntityId(id)}`), [navigate]);
  const editUnit = useCallback((id: string) => navigate(`/units/${encodeEntityId(id)}/edit`), [navigate]);
  const addUnder = useCallback(
    (id: string) => {
      const u = model.byId[id];
      const qs = new URLSearchParams({ parentUnitId: id, parentUnitName: u?.name || '' });
      navigate(`/units/create?${qs.toString()}`);
    },
    [model, navigate]
  );

  /**
   * Open Personnel Master with this unit pre-applied as the personnel filter.
   *
   * The name rides along in the URL. Personnel Master has the id it needs to
   * filter, but nothing to say WHICH unit it is filtered to, and "showing 40 of
   * 1,377 people" with no explanation reads as a broken list. Sending the name
   * we already have here saves that screen a lookup it would otherwise have to
   * make on arrival.
   */
  const openPeople = useCallback(
    (id: string) => {
      const u = model.byId[id];
      const qs = new URLSearchParams({ unitId: id });
      if (u?.name) qs.set('unitName', u.name);
      navigate(`/personnel?${qs.toString()}`);
    },
    [model, navigate]
  );

  const selectedUnit = selected ? model.byId[selected] : null;
  const onChart = isChartView(view);
  /** Everything except Compare is a single tree the depth and focus controls act on. */
  const onTree = view !== 'compare';
  const current = VIEWS.find((v) => v.key === view)!;
  const projectName = projects.find((p: AccessibleProject) => p._id === projectId)?.name;
  const overallVacancy =
    model.totalPosts > 0 ? Math.round(((model.totalPosts - model.totalFilled) / model.totalPosts) * 100) : null;
  const levelsTotal = model.ids.length ? model.maxLevel + 1 : 0;
  /** `known` = the counts are real. `ready` = there is a tree to drive controls. */
  const known = !isLoading && !error;
  const ready = known && model.ids.length > 0;

  const closeRail = (open: boolean) => {
    setRailOpen(open);
    localStorage.setItem(RAIL_KEY, open ? 'open' : 'closed');
  };
  const dismissHint = () => {
    setHintSeen(true);
    localStorage.setItem(HINT_KEY, 'yes');
  };


  /** Left and right walk the tab strip, as a tab strip is expected to. */
  const onTabsKey = (e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const i = VIEWS.findIndex((v) => v.key === view);
    const next = VIEWS[(i + step + VIEWS.length) % VIEWS.length];
    setView(next.key);
    if (next.key !== 'compare') clearSelection();
    document.getElementById(`orgtab-${next.key}`)?.focus();
  };

  /** Reading the vacancy chip and acting on it are the same gesture. */
  const focusVacant = () => {
    if (!onChart) setView('chart');
    setVacantOnly((v) => !v);
  };

  return (
    <div ref={outerRef} className={`${styles.shell}${embedded ? '' : ` ${styles.shellPage}`}`}>
      <div
        ref={shellRef}
        className={`${styles.wrap}${fullscreen.mode === 'fallback' ? ` ${styles.fsfallback}` : ''}`}
        style={{ flex: 1, minHeight: 0 }}
      >
        {/* Inside the shell on purpose — the browser paints nothing outside the
            full-screen element, so a toast parked next to it would vanish. */}
        <Toast ref={toast} />
        {railOpen && (
          <div className={styles.rail}>
            {!embedded && (
              <>
                <span className={styles.railicon}>
                  <Icon name="sitemap" className={styles.ic} />
                </span>
                <div className={styles.railid}>
                  <b>Org structure</b>
                  <em>{projectName || 'All accessible projects'}</em>
                </div>
              </>
            )}

            <div className={styles.kpis}>
              <div className={styles.kpi}>
                <b>{known ? nf(model.ids.length) : '\u2014'}</b>
                <span>Units</span>
              </div>

              {/* Each chip is dropped rather than dashed when the source behind
                  it did not answer. A permanent em dash teaches nothing, and a
                  row of them reads as a broken screen rather than missing data. */}
              {(!known || model.hasHeadcount) && (
                <div className={styles.kpi} title="Personnel across every unit in scope">
                  <b>{known ? nf(model.totalPeople) : '\u2014'}</b>
                  <span>People</span>
                </div>
              )}

              {(!known || model.hasPostData) && (
                <>
                  <div className={styles.kpi} title="Filled of sanctioned posts across every unit in scope">
                    <b>{known ? `${nf(model.totalFilled)}/${nf(model.totalPosts)}` : '\u2014'}</b>
                    <span>Posts</span>
                  </div>
                  <div className={styles.kpi} title="Share of sanctioned posts standing empty">
                    <b style={{ color: vacancyTone(overallVacancy) }}>
                      {known && overallVacancy !== null ? `${overallVacancy}%` : '\u2014'}
                    </b>
                    <span>Unfilled</span>
                  </div>
                  {/* A control only where there is something to act on: with no
                      vacancies this is a fact, and a dead button is worse than none. */}
                  {ready && model.vacantCount > 0 ? (
                    <button aria-label="Show only the units whose head post stands empty"
                      type="button"
                      className={`${styles.kpi} ${styles.kpialert}${vacantOnly ? ` ${styles.kpion}` : ''}`}
                      aria-pressed={vacantOnly}
                      title="Show only the units whose head post stands empty"
                      onClick={focusVacant}
                    >
                      <b>{nf(model.vacantCount)}</b>
                      <span>Vacant</span>
                    </button>
                  ) : (
                    <div className={styles.kpi} title="Every head post on record is filled">
                      <b>{known ? nf(model.vacantCount) : '\u2014'}</b>
                      <span>Vacant</span>
                    </div>
                  )}
                </>
              )}

              {/* "Levels" and "Top level" used to sit here. The first said the
                  same thing as the depth control now says on its own face, and
                  said it in a form — 6/6 — that needed decoding first. The
                  second counted a technicality: how many units happen to have
                  nothing above them in the current scope, which is a fact about
                  the query, not about the force. */}
            </div>

            <div className={styles.railact}>
              {projects.length > 1 && (
                <select
                  value={projectId ?? ''}
                  aria-label="Project"
                  onChange={(e) => setProjectId(e.target.value || null)}
                >
                  <option value="">All accessible projects</option>
                  {projects.map((p: AccessibleProject) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              <button aria-label="Re-read the units and the headcount from the server" type="button" className={styles.btn} title="Re-read the units and the headcount from the server" onClick={() => refetch()} disabled={isFetching}>
                <Icon name="refresh" className={styles.ic} />
                {isFetching ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                className={styles.railtog}
                title="Hide the summary"
                aria-label="Hide the summary"
                aria-expanded
                onClick={() => closeRail(false)}
              >
                <Icon name="down" className={`${styles.ic} ${styles.chev}`} />
              </button>
            </div>
          </div>
        )}

        {(ready || !railOpen) && (
        <div className={styles.viewbar}>
          {!railOpen && (
            <>
              <button aria-label="Show the summary"
                type="button"
                className={styles.btn}
                title="Show the summary"
                onClick={() => closeRail(true)}
              >
                <Icon name="sitemap" className={styles.ic} />
                {nf(model.ids.length)} units
                {model.vacantCount ? ` · ${nf(model.vacantCount)} vacant` : ''}
              </button>
              <span className={styles.vsep} />
            </>
          )}

          {/* Nothing to switch between and nothing to filter until a tree has
              actually arrived, so these come out rather than sit there inert. */}
          {/* A tablist whose children are not all tabs, that answered no arrow
              key and had no panel to point at, was announcing a contract it did
              not keep. It keeps it now: the captions are decoration to a screen
              reader, one tab holds the tab stop, and the arrows move between
              them the way a tab strip is expected to. */}
          {ready && (
          <div className={styles.seg} role="tablist" aria-label="View" onKeyDown={onTabsKey}>
            {VIEWS.map((v, i) => (
              <Fragment key={v.key}>
                {(i === 0 || VIEWS[i - 1].group !== v.group) && (
                  <>
                    {i > 0 && <span className={styles.sp} role="presentation" />}
                    <span className={styles.cap} role="presentation">
                      {v.group}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  role="tab"
                  id={`orgtab-${v.key}`}
                  aria-controls="orgtabpanel"
                  aria-selected={view === v.key}
                  tabIndex={view === v.key ? 0 : -1}
                  title={v.what}
                  className={view === v.key ? styles.segon : undefined}
                  onClick={() => {
                    setView(v.key);
                    if (v.key !== 'compare') clearSelection();
                  }}
                >
                  {v.label}
                </button>
              </Fragment>
            ))}
          </div>
          )}

          {/* Show a control only where it does something. Depth, spotlight and
              the vacancy filter all act on the chart layout, so on Compare they
              come out rather than sit greyed — a disabled control still costs a
              slot and still invites a click. */}
          {ready && (
          <div className={styles.grp}>
            {/* How deep to unfold, a level at a time, with what it costs
                beside it. The count is what makes this a decision rather than
                a guess. */}
            {onTree && (
              <span className={styles.lvlseg} role="group" aria-label="How many levels of the tree to show">
                <button
                  type="button"
                  className={styles.lvlb}
                  disabled={depth <= 0}
                  aria-label="Show one level fewer"
                  title="One level fewer"
                  onClick={() => step(depth - 1)}
                >
                  <span className={styles.lvlsign} aria-hidden="true">−</span>
                </button>
                <span className={styles.lvlval}>
                  <b>
                    {Math.min(depth + 1, levelsTotal)} of {levelsTotal} levels
                  </b>
                  {/* Announced, because the number is the whole point of the
                      control and a screen reader would otherwise hear only
                      that a button was pressed. */}
                  <i aria-live="polite">{nf(shownUnits)} units</i>
                </span>
                <button
                  type="button"
                  className={styles.lvlb}
                  disabled={depth >= deepest}
                  aria-label="Show one level more"
                  title={depth >= deepest ? 'Every level is showing' : 'One level more'}
                  onClick={() => step(depth + 1)}
                >
                  <span className={styles.lvlsign} aria-hidden="true">+</span>
                </button>
              </span>
            )}
            <OrgSearch
              model={model}
              value={rawQuery}
              onChange={setRawQuery}
              query={query}
              vacantOnly={vacantOnly}
              onPick={goToUnit}
              compareSet={columns}
              onToggleCompare={toggleCompare}
              pickVerb={view === 'compare' ? 'add it to a column' : 'go to it'}
            />
            {onTree && (
              <>
                <button aria-label="When a unit is picked, show only its line of command — every level above it and everything under it"
                  type="button"
                  className={`${styles.btn}${isolate ? ` ${styles.on}` : ''}`}
                  aria-pressed={isolate}
                  title="When a unit is picked, show only its line of command — every level above it and everything under it"
                  onClick={() => {
                    setIsolate((v) => !v);
                    if (selected) reframe('home');
                  }}
                >
                  Isolate line
                </button>
                <button aria-label="Show only the units whose head post stands empty"
                  type="button"
                  className={`${styles.btn}${vacantOnly ? ` ${styles.on}` : ''}`}
                  aria-pressed={vacantOnly}
                  title="Show only the units whose head post stands empty"
                  onClick={() => setVacantOnly((v) => !v)}
                >
                  Vacant only
                </button>
              </>
            )}
          </div>
          )}

          <div className={`${styles.grp} ${styles.acts}`}>
            {canCreate && (
              <button aria-label="Create a new unit" type="button" className={`${styles.btn} ${styles.pri}`} title="Create a new unit" onClick={() => navigate('/units/create')}>
                <Icon name="plus" className={styles.ic} />
                Add unit
              </button>
            )}
            <button
              type="button"
              className={`${styles.btn}${fullscreen.active ? ` ${styles.on}` : ''}`}
              title={fullscreen.active ? 'Leave full screen (Esc)' : 'Give the chart the whole screen'}
              aria-pressed={fullscreen.active}
              onClick={fullscreen.toggle}
            >
              <Icon name={fullscreen.active ? 'collapse' : 'expand'} className={styles.ic} />
              {fullscreen.active ? 'Exit full screen' : 'Full screen'}
            </button>
            <button
              type="button"
              className={`${styles.btn}${notesOpen ? ` ${styles.on}` : ''}`}
              aria-pressed={notesOpen}
              onClick={() => setNotesOpen((v) => !v)}
             title="What each figure on this screen means">
              About this view
            </button>
          </div>
        </div>
        )}

        {notesOpen && (
          <p className={styles.notebar}>
            <span>
              <b>{current.label}:</b> {current.what}
            </span>
            <span>
              <b>Figures:</b> <code>People</code> counts personnel across a unit and everything under it.{' '}
              <code>Posts</code> is filled of sanctioned, from each unit’s own post list, and{' '}
              <code>Unfilled</code> is the share of those posts standing empty — an em dash where a unit has no
              posts on record, which is not the same as fully staffed. A unit reads as vacant when no one holds
              its head post.
            </span>
          </p>
        )}

        <div
          className={styles.canvas}
          id="orgtabpanel"
          role="tabpanel"
          aria-labelledby={ready ? `orgtab-${view}` : undefined}
        >
          {error ? (
            <div className={styles.state}>
              <span className={`${styles.stateicon} ${styles.statealert}`}>
                <Icon name="alert" className={styles.ic} />
              </span>
              <h3>The structure could not be loaded</h3>
              <p>
                <code>{error.message}</code>
                <br />
                Nothing has changed on your side — the tree is read-only.
              </p>
              <div className={styles.stateact}>
                <button aria-label="Run the request again" type="button" className={`${styles.btn} ${styles.pri}`} title="Run the request again" onClick={() => refetch()}>
                  <Icon name="refresh" className={styles.ic} />
                  Try again
                </button>
              </div>
            </div>
          ) : isLoading ? (
            <div className={styles.state}>
              <div className={styles.skelrow}>
                <div className={styles.skel} style={{ width: 272, height: 74 }} />
              </div>
              <div className={styles.skelwire} />
              <div className={styles.skelrow}>
                <div className={styles.skel} style={{ width: 216, height: 62 }} />
                <div className={styles.skel} style={{ width: 216, height: 62 }} />
                <div className={styles.skel} style={{ width: 216, height: 62 }} />
              </div>
              <p style={{ marginTop: '1.4rem' }}>Building the tree and its rollups…</p>
            </div>
          ) : !model.ids.length ? (
            <div className={styles.state}>
              <span className={styles.stateicon}>
                <Icon name="building" className={styles.ic} />
              </span>
              <h3>No units to chart yet</h3>
              <p>
                {projectId
                  ? 'This project has no active units, or none you have permission to see. The chart appears as soon as the first one exists.'
                  : 'Nothing active in any project you can access. The chart appears as soon as the first unit exists.'}
              </p>
              <div className={styles.stateact}>
                {canCreate && (
                  <button aria-label="Create the first unit in this project"
                    type="button"
                    className={`${styles.btn} ${styles.pri}`}
                    title="Create the first unit in this project" onClick={() => navigate('/units/create')}
                  >
                    <Icon name="plus" className={styles.ic} />
                    Add the first unit
                  </button>
                )}
                <button aria-label="Run the request again" type="button" className={styles.btn} title="Run the request again" onClick={() => refetch()} disabled={isFetching}>
                  <Icon name="refresh" className={styles.ic} />
                  Refresh
                </button>
              </div>
            </div>
          ) : view === 'outline' ? (
            <OrgOutline
              model={model}
              collapsed={collapsed}
              onToggleCollapse={toggleCollapse}
              selected={selected}
              onSelect={selectUnit}
              query={query}
              vacantOnly={vacantOnly}
              isolate={isolate}
              compareSet={columns}
              onToggleCompare={toggleCompare}
              onOpenUnit={openUnit}
              focus={focus}
            />
          ) : onChart ? (
            <OrgChart
              model={model}
              view={view}
              collapsed={collapsed}
              onToggleCollapse={toggleCollapse}
              selected={selected}
              onSelect={selectUnit}
              query={query}
              vacantOnly={vacantOnly}
              isolate={isolate}
              compareSet={columns}
              onToggleCompare={toggleCompare}
              fitToken={fitToken}
              framingMode={frame.mode}
              focus={focus}
              hint={!hintSeen && !selected ? dismissHint : undefined}
              onShowDetails={() => setDetailsOpen(true)}
            />
          ) : (
            <OrgCompare
              model={model}
              columns={columns}
              onSetColumn={setColumn}
              onRemoveColumn={removeColumn}
              onToggleCompare={toggleCompare}
              onClear={() => {
                const n = columns.length;
                setColumns([]);
                notify(`Cleared ${n} unit${n === 1 ? '' : 's'} from the compare set`);
              }}
              onShowOnChart={showOnChart}
              onOpenUnit={openUnit}
              onEditUnit={canUpdate ? editUnit : undefined}
              onAddUnder={canCreate ? addUnder : undefined}
            />
          )}
        </div>

        {/* The details, over the canvas rather than inside it.
            Dismissing the popup leaves the line of command on the chart — the
            banner there offers it back, and Escape again clears the lot. */}
        {selectedUnit && detailsOpen && (
          <div
            ref={popRef}
            className={styles.popback}
            role="presentation"
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) setDetailsOpen(false);
            }}
          >
            <OrgInspector
              model={model}
              unit={selectedUnit}
              onSelect={(id) => (id ? selectUnit(id) : setDetailsOpen(false))}
              stored={columns.includes(selectedUnit.id)}
              onToggleCompare={toggleCompare}
              onOpenUnit={openUnit}
              onEditUnit={canUpdate ? editUnit : undefined}
              onAddUnder={canCreate ? addUnder : undefined}
              onOpenPeople={openPeople}
            />
          </div>
        )}

        {/* The tray follows you between views, so a compare set survives a trip
            back to the chart to find the next unit for it. */}
        {columns.length > 0 && (
          <div className={styles.tray}>
            <span className={styles.tl}>
              Compare set <b>{columns.length}</b>/{MAX_COMPARE}
            </span>
            {columns.map((id) => {
              const u = model.byId[id];
              if (!u) return null;
              return (
                <span key={id} className={`${styles.chip}${selected === id ? ` ${styles.chipon}` : ''}`}>
                  <button
                    type="button"
                    className={styles.lb}
                    title={`Show ${u.name} on the chart`}
                    onClick={() => showOnChart(id)}
                  >
                    <span
                      className={styles.sq}
                      style={{ background: headColour(u) }}
                    />
                    <b>{u.name}</b>
                    <span className={styles.n}>{peopleLabel(model, u)}</span>
                  </button>
                  <button
                    type="button"
                    className={styles.x}
                    aria-label={`Remove ${u.name}`}
                    title={`Remove ${u.name} from the compare set`}
                    onClick={() => toggleCompare(id)}
                  >
                    <Icon name="x" className={styles.ic} size={11} />
                  </button>
                </span>
              );
            })}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '.4rem', flex: 'none' }}>
              {view !== 'compare' && (
                <button aria-label="Open the Compare view with this set" type="button" className={`${styles.btn} ${styles.pri}`} title="Open the Compare view with this set" onClick={() => setView('compare')}>
                  Compare {columns.length}
                </button>
              )}
              <button aria-label="Empty the compare set"
                type="button"
                className={styles.btn}
                title="Empty the compare set" onClick={() => {
                  const n = columns.length;
                  setColumns([]);
                  notify(`Cleared ${n} unit${n === 1 ? '' : 's'} from the compare set`);
                }}
              >
                Clear
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgStructureView;
